import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  blockUser,
  dropConnection,
  getBlocks,
  getConnections,
  getFamily,
  getWhoCanInvite,
  setWhoCanInvite,
  unblockUser,
  type PortalConnection,
  type WhoCanInvite,
} from '../lib/api.js';
import { connectionDetail, connectionLabel, connectionPath, initialOf } from '../lib/connections.js';
import { FAMILY_PATH } from '../lib/family.js';
import { Modal } from '../components/Modal.js';
import { Note, Problem, button } from '../components/form.js';
import { Select } from '../components/Select.js';

/**
 * The people this account is connected to, and the people it has blocked
 * (RidgeWill/account#79, over RidgeWill/auth#486).
 *
 * ── WHY THIS LIVES IN THE PORTAL ─────────────────────────────────────
 *
 * adr/0012 made connections a platform relationship, and adr/0010 puts account
 * surfaces in the portal rather than in each product. So this is the one place a
 * connection is managed across every Ridgewill app, and products link here.
 *
 * ── REMOVING IS NOT BLOCKING, AND THE SCREEN HAS TO SAY SO ───────────
 *
 * A removal takes the connection off both lists and leaves the pathway open:
 * either person can connect again and an invitation can still arrive. Somebody
 * reaching for "remove" to get away from a person has stopped nothing. So the
 * two are different buttons with different dialogs, and the removal dialog
 * offers the block in the same place the person is about to make the mistake.
 *
 * ── A BLOCK IS NOT ANNOUNCED ─────────────────────────────────────────
 *
 * The person blocked is not told, and auth takes the blocker off their lists
 * (Jon, 2026-09-13). This screen therefore never shows who has blocked the
 * reader: the service does not send those rows to it at all.
 *
 * ── THE BLOCK LIST OPENS FROM THE TOP, IN A DIALOG ───────────────────
 *
 * account#79: somebody looking for "who have I blocked" will not think to look
 * inside a relationship they are trying to forget, so it is never only reachable
 * through a connection.
 *
 * It was a second section under the connections, and Jon moved it (2026-09-13):
 * somebody with a lot of connections would have their blocked people pushed to
 * the very bottom of a long page. So "Blocked people" is a control ABOVE the
 * list, and the list opens in a dialog, which scrolls on its own. The dialog is
 * still addressable as `/connections#blocked`.
 *
 * ── WHO CAN INVITE YOU SITS AT THE TOP TOO ───────────────────────────
 *
 * account#91: beside Blocked people, for the same reason, so a long list never
 * pushes it out of reach. It is addressable as `/connections#who-can-invite`,
 * which is where the opt-out page's confirmation sends people.
 *
 * ── NOT HERE YET, BECAUSE THE SERVICE CANNOT ANSWER IT ───────────────
 *
 * Which apps each connection is active in (a connection records no app), and
 * per-app controls from a session. account#79 keeps them open; nothing is drawn
 * for them rather than controls that could only fail.
 */
export function Connections() {
  const [connections, setConnections] = useState<PortalConnection[]>([]);
  const [blocks, setBlocks] = useState<PortalConnection[]>([]);
  /** Tracked apart from the lists, as in Subscriptions: `[]` is a real answer. */
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PortalConnection | null>(null);
  const [blocking, setBlocking] = useState<PortalConnection | null>(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const [invite, setInvite] = useState<WhoCanInvite>('anyone');
  /**
   * Whether to offer "Create family group" (account#117): auth's `mayStart`,
   * and only while this person is in no group. A failed read offers nothing,
   * rather than a link to a page that could only refuse.
   */
  const [offerFamily, setOfferFamily] = useState(false);

  /**
   * The dialog owns `#blocked` in the address, so the open list can be linked
   * and a reload does not reopen a list somebody closed. `replaceState` rather
   * than assigning the hash: neither opening nor closing should become a Back
   * step.
   */
  function openBlocked() {
    setError(null);
    setShowBlocked(true);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#blocked`);
  }

  function closeBlocked() {
    setShowBlocked(false);
    if (window.location.hash === '#blocked') {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }

  async function load() {
    try {
      const [mine, blocked, setting, family] = await Promise.all([
        getConnections(),
        getBlocks(),
        getWhoCanInvite(),
        getFamily().catch(() => null),
      ]);
      setOfferFamily(Boolean(family?.mayStart) && (family?.groups ?? []).length === 0);
      // `?? []` for the reason `getCurrentAgreements` records: `call<T>` casts,
      // so a renamed key is `undefined` at runtime rather than a type error.
      setConnections(mine.connections ?? []);
      setBlocks(blocked.blocks ?? []);
      setInvite(setting.whoCanInvite === 'connections' ? 'connections' : 'anyone');
      setLoaded(true);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your connections.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  /**
   * A deep link lands on its row once the rows exist. The browser's own jump
   * happens at load, before this screen has fetched anything, so it finds
   * nothing to scroll to.
   */
  useEffect(() => {
    if (!loaded) return;
    const target = window.location.hash.slice(1);
    if (target === 'blocked') setShowBlocked(true);
    else if (target) document.getElementById(target)?.scrollIntoView?.({ block: 'start' });
  }, [loaded]);

  /** Run one change and reload. Refusals show the service's own sentence. */
  async function act(key: string, change: () => Promise<unknown>, failure: string): Promise<boolean> {
    setBusy(key);
    try {
      await change();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : failure);
      return false;
    } finally {
      setBusy(null);
    }
  }

  if (error && !loaded) return <Problem>{error}</Problem>;
  if (!loaded) return <Note>Loading…</Note>;

  return (
    <>
      <section style={{ maxWidth: 640 }}>
        <div style={header}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 .35rem' }}>Connections</h2>
            <Note>People you are connected with through any Ridgewill app.</Note>
          </div>
          {/* Above the list, whatever its length. The count says whether there
              is anything behind it before somebody opens it. "Create family
              group" sits beside it in the same quiet style: an occasional
              task, one tap away, that a long list never pushes out of reach. */}
          <div style={headerLinks}>
            <button type="button" onClick={openBlocked} style={blockedLink}>
              Blocked people{blocks.length > 0 ? ` (${blocks.length})` : ''}
            </button>
            {offerFamily && (
              <a href={FAMILY_PATH} style={blockedLink}>
                Create family group
              </a>
            )}
          </div>
        </div>

        <WhoCanInviteControl value={invite} onSaved={setInvite} />

        {/* Not while a dialog is open: the dialog shows it, in front of the
            person, rather than on the page behind the backdrop. */}
        {error && !removing && !blocking && !showBlocked && <Problem>{error}</Problem>}

        {connections.length === 0 ? (
          <Note>You are not connected with anyone yet.</Note>
        ) : (
          <ul style={list}>
            {connections.map((c) => (
              <PersonRow key={c.id} anchor={`connection-${c.id}`} connection={c}>
                {c.state === 'pending' ? (
                  <button
                    type="button"
                    disabled={busy === c.id}
                    onClick={() =>
                      void act(c.id, () => dropConnection(c.id), 'That invitation could not be withdrawn.')
                    }
                    style={rowButton}
                  >
                    {busy === c.id ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                ) : (
                  <>
                    {/* A Family connection is not removed here: the way apart
                        is the Family Group (auth#614 refuses the drop). */}
                    {c.family ? (
                      <a href={FAMILY_PATH} style={{ ...rowButton, textDecoration: 'none', display: 'inline-block' }}>
                        Family Group
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setRemoving(c);
                        }}
                        style={rowButton}
                      >
                        Remove
                      </button>
                    )}
                    {c.userId && (
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setBlocking(c);
                        }}
                        style={rowButton}
                      >
                        Block
                      </button>
                    )}
                  </>
                )}
              </PersonRow>
            ))}
          </ul>
        )}
      </section>

      <BlockedDialog
        open={showBlocked}
        blocks={blocks}
        busy={busy}
        error={showBlocked ? error : null}
        onUnblock={(b) =>
          void act(b.id, () => unblockUser(b.userId as string), 'That block could not be lifted.')
        }
        onClose={closeBlocked}
      />

      <RemoveDialog
        target={removing}
        onClose={() => setRemoving(null)}
        onRemove={async (c) => {
          if (await act(c.id, () => dropConnection(c.id), 'That connection could not be removed.')) {
            setRemoving(null);
          }
        }}
        onBlockInstead={(c) => {
          setError(null);
          setRemoving(null);
          setBlocking(c);
        }}
        busy={removing ? busy === removing.id : false}
        error={removing ? error : null}
      />

      <BlockDialog
        target={blocking}
        error={blocking ? error : null}
        onClose={() => setBlocking(null)}
        onBlock={async (c) => {
          if (c.userId && (await act(c.id, () => blockUser(c.userId as string), 'That account could not be blocked.'))) {
            setBlocking(null);
          }
        }}
        busy={blocking ? busy === blocking.id : false}
      />
    </>
  );
}

const INVITE_LABELS: Record<WhoCanInvite, string> = {
  anyone: 'Anyone',
  connections: "Only people I'm connected with",
};

/**
 * Who can invite you (account#91). Saved as soon as it changes, like the theme
 * control, and put back if the save fails so the page never shows a setting
 * the service does not hold. Its own error line, because the page-level one
 * sits under it and would read as being about the connections list.
 */
function WhoCanInviteControl({
  value,
  onSaved,
}: {
  value: WhoCanInvite;
  onSaved: (value: WhoCanInvite) => void;
}) {
  const [shown, setShown] = useState<WhoCanInvite>(value);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => setShown(value), [value]);

  async function change(next: WhoCanInvite) {
    setShown(next);
    setSaving(true);
    setFailure(null);
    try {
      const saved = await setWhoCanInvite(next);
      onSaved(saved.whoCanInvite);
    } catch (e) {
      setShown(value);
      setFailure(e instanceof Error ? e.message : 'That setting could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div id="who-can-invite" style={inviteBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap' }}>
        <span id="who-can-invite-label" style={{ fontWeight: 500 }}>
          Who can invite you
        </span>
        <Select
          labelledBy="who-can-invite-label"
          value={shown}
          disabled={saving}
          options={(Object.keys(INVITE_LABELS) as WhoCanInvite[]).map((option) => ({
            value: option,
            label: INVITE_LABELS[option],
          }))}
          onChange={(next) => void change(next)}
        />
      </div>
      <Note>
        {"People you're not connected with can't invite you or add you to anything, in any Ridgewill app."}
      </Note>
      {failure && <Problem>{failure}</Problem>}
    </div>
  );
}

function PersonRow({
  connection,
  anchor,
  children,
  detail: withDetail = true,
}: {
  connection: PortalConnection;
  anchor: string;
  children: React.ReactNode;
  /** Off in the block list, where the dialog's opening sentence already says it
   *  once and repeating it under every name is noise. */
  detail?: boolean;
}) {
  const label = connectionLabel(connection);
  const detail = withDetail ? connectionDetail(connection) : null;
  /**
   * Only an ACTIVE connection's name opens its page. An invitation has nobody on
   * the other end yet, and a blocked person's page would offer nothing the block
   * list does not already.
   */
  const page = connection.state === 'active' ? connectionPath(connection.id) : null;
  return (
    <li id={anchor} style={row}>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'flex-start', flex: '1 1 16rem', minWidth: 0 }}>
        <Picture image={connection.person?.image ?? null} initial={initialOf(label)} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
            {page ? (
              <a href={page} style={{ color: 'inherit', textUnderlineOffset: '.2em' }}>
                {label.title}
              </a>
            ) : (
              label.title
            )}
          </div>
          {label.handle && <Note>{label.handle}</Note>}
          {connection.family && <FamilyMark />}
          {detail && <Note>{detail}</Note>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>{children}</div>
    </li>
  );
}

/** The Family mark on a connection (account#117): both are in one Family Group. */
export function FamilyMark() {
  return (
    <span
      style={{
        display: 'inline-block',
        marginTop: '.3rem',
        padding: '1px 8px',
        borderRadius: 999,
        background: C.blueTint,
        color: C.blueText,
        fontSize: '.75rem',
        fontWeight: 600,
      }}
    >
      Family
    </span>
  );
}

export function Picture({
  image,
  initial,
  size = 40,
}: {
  image: string | null;
  initial: string;
  size?: number;
}) {
  const shape = { ...circle, width: size, height: size };
  if (image) {
    return (
      <img
        src={image}
        alt=""
        width={size}
        height={size}
        // A social sign-in picture lives on a third-party CDN; no referrer, as
        // on the Profile screen.
        referrerPolicy="no-referrer"
        style={{ ...shape, objectFit: 'cover' }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        ...shape,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: C.textMid,
        fontSize: size > 40 ? '1.4rem' : undefined,
      }}
    >
      {initial}
    </span>
  );
}

export function RemoveDialog({
  target,
  onClose,
  onRemove,
  onBlockInstead,
  busy,
  error,
}: {
  target: PortalConnection | null;
  onClose: () => void;
  onRemove: (c: PortalConnection) => void | Promise<void>;
  onBlockInstead: (c: PortalConnection) => void;
  busy: boolean;
  error: string | null;
}) {
  const [last, setLast] = useState<PortalConnection | null>(target);
  useEffect(() => {
    if (target) setLast(target);
  }, [target]);
  // Kept after close so the dialog can fade out with its words still in it.
  const shown = target ?? last;
  const name = shown ? connectionLabel(shown).title : '';

  return (
    <Modal open={target !== null} title="Remove this connection?" onClose={onClose}>
      <p style={{ margin: '0 0 .75rem', lineHeight: 1.55 }}>
        {name} will no longer be one of your connections, and you will no longer be one of theirs.
      </p>
      <p style={{ margin: '0 0 1.25rem', lineHeight: 1.55 }}>
        <strong>Removing does not stop them reaching you.</strong> Either of you can connect again, and
        they can still invite you. To stop that, block them instead.
      </p>
      {error && <Problem>{error}</Problem>}
      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy || !shown}
          onClick={() => shown && void onRemove(shown)}
          style={{ ...button, marginTop: 0 }}
        >
          {busy ? 'Removing…' : 'Remove connection'}
        </button>
        {shown?.userId && (
          <button type="button" disabled={busy} onClick={() => onBlockInstead(shown)} style={{ ...button, marginTop: 0 }}>
            Block instead
          </button>
        )}
        <button type="button" disabled={busy} onClick={onClose} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

export function BlockDialog({
  target,
  onClose,
  onBlock,
  busy,
  error,
}: {
  target: PortalConnection | null;
  onClose: () => void;
  onBlock: (c: PortalConnection) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const [last, setLast] = useState<PortalConnection | null>(target);
  useEffect(() => {
    if (target) setLast(target);
  }, [target]);
  const shown = target ?? last;
  const name = shown ? connectionLabel(shown).title : 'this account';

  return (
    <Modal open={target !== null} title={`Block ${name}?`} onClose={onClose}>
      <p style={{ margin: '0 0 .75rem', lineHeight: 1.55 }}>
        They will not be able to connect with you, invite you or add you to anything in any Ridgewill
        app, and they are not told that you blocked them.
      </p>
      {shown?.family && (
        <p style={{ margin: '0 0 .75rem', lineHeight: 1.55 }}>
          {shown.familyOnBlock === 'you-leave'
            ? `You also leave your Family Group with ${name}.`
            : shown.familyOnBlock === 'they-leave'
              ? `${name} is also removed from your Family Group.`
              : 'You will also no longer be in the same Family Group.'}{' '}
          If the person who leaves is under 18, anything in a Ridgewill app that needs a parent or guardian is
          locked until another Family Group adds them.
        </p>
      )}
      <p style={{ margin: '0 0 1.25rem', lineHeight: 1.55 }}>
        Anything an app already keeps about what you did together stays in that app. You can unblock
        them later from Blocked people on this page.
      </p>
      {error && <Problem>{error}</Problem>}
      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy || !shown}
          onClick={() => shown && void onBlock(shown)}
          style={{ ...button, marginTop: 0 }}
        >
          {busy ? 'Blocking…' : 'Block'}
        </button>
        <button type="button" disabled={busy} onClick={onClose} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

/**
 * Everyone this account has blocked, with a way to lift each block.
 *
 * A dialog rather than a section, so its place does not depend on how many
 * connections sit above it. The Modal panel scrolls on its own past 90vh, so a
 * long block list stays usable too.
 */
function BlockedDialog({
  open,
  blocks,
  busy,
  error,
  onUnblock,
  onClose,
}: {
  open: boolean;
  blocks: PortalConnection[];
  busy: string | null;
  error: string | null;
  onUnblock: (b: PortalConnection) => void;
  onClose: () => void;
}) {
  return (
    <Modal open={open} title="Blocked people" onClose={onClose}>
      <p style={{ margin: 0, lineHeight: 1.55 }}>
        They cannot connect with you or invite you in any Ridgewill app, and they were not told.
        Unblocking lets them do both again.
      </p>
      {error && <Problem>{error}</Problem>}
      {blocks.length === 0 ? (
        <Note>You have not blocked anyone.</Note>
      ) : (
        <ul style={list}>
          {blocks.map((b) => (
            <PersonRow key={b.id} anchor={`blocked-${b.id}`} connection={b} detail={false}>
              {b.userId && (
                <button
                  type="button"
                  disabled={busy === b.id}
                  onClick={() => onUnblock(b)}
                  style={rowButton}
                >
                  {busy === b.id ? 'Unblocking…' : 'Unblock'}
                </button>
              )}
            </PersonRow>
          ))}
        </ul>
      )}
      <div style={{ marginTop: '1.25rem' }}>
        <button type="button" onClick={onClose} style={{ ...button, marginTop: 0 }}>
          Close
        </button>
      </div>
    </Modal>
  );
}

const list: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '1rem 0 0' };

const inviteBox: React.CSSProperties = { margin: '1rem 0 0' };


/** The links at the top right: they stack on a phone and sit side by side when there is room. */
const headerLinks: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '.4rem',
};

const header: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '1rem',
  flexWrap: 'wrap',
};

/** Reads as a link and is a button: it opens a dialog rather than navigating. */
const blockedLink: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: C.brand,
  fontSize: '.9rem',
  textDecoration: 'underline',
  textUnderlineOffset: '.2em',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap',
  padding: '.85rem 0',
  borderTop: `1px solid ${C.inputBorder}`,
};

const rowButton: React.CSSProperties = { ...button, marginTop: 0, whiteSpace: 'nowrap' };

const circle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: `1px solid ${C.inputBorder}`,
  flexShrink: 0,
};
